<template>
  <div
    class="d-flex w-full h-100 align-items-center justify-content-center p-4"
    id="page-login"
  >
    <div class="col-md-6 col-lg-6 col-xl-4 px-0">
      <b-card>
        <div class="justify-center items-center">
          <div class="login-container w-full p-2">
            <div v-if="invalidationMessage">
              <div class="alert alert-danger" role="alert">
                <h5>{{ $t("user.forgot.recover_account_failure") }}</h5>
                <p v-html="$t('user.forgot.contact_to', { company_name })"></p>
              </div>
              <div class="mb-4">
                <b-button
                  class="btn w-full border"
                  variant="link"
                  pill
                  @click="$router.push({ name: 'login' })"
                >
                  {{ $t("user.forgot.return_to") }}
                </b-button>
              </div>
            </div>

            <div class="mb-6" v-if="!invalidationMessage">
              <div>
                <h5>{{ $t("user.forgotAccount.title") }}</h5>
                <p>{{ $t("user.forgotAccount.info") }}</p>
              </div>
            </div>

            <div class="mb-6" v-if="showCaptchaAlert">
              <div class="alert alert-danger" role="alert">
                {{ $t("common.captcha_error") }}
              </div>
            </div>

            <validation-observer ref="confirmAccountInfoRules">
              <div class="pb-4" v-if="!invalidationMessage">
                <form-field-input
                  ref="currentField"
                  :key="'recover-step-cpf'"
                  name="passwords.cpf"
                  v-model="passwords.cpf"
                  rules="required|cpf"
                  mask="###.###.###-##"
                  autocomplete="off"
                  inputmode="numeric"
                  v-if="currentStepKey === 'cpf'"
                />

                <form-field-telephone
                  ref="currentField"
                  :key="'recover-step-cellphone'"
                  name="passwords.cellphone"
                  v-model="passwords.cellphone"
                  rules="required|cellphone"
                  v-if="currentStepKey === 'cellphone'"
                />

                <form-field-date
                  ref="currentField"
                  :key="'recover-step-birth_date'"
                  name="passwords.birth_date"
                  v-model="passwords.birth_date"
                  rules="date|required|minority-date"
                  inputmode="numeric"
                  v-if="currentStepKey === 'birth_date'"
                />

                <form-field-input
                  ref="currentField"
                  :key="'recover-step-mother_name'"
                  name="passwords.mother_name"
                  v-model="passwords.mother_name"
                  rules="required"
                  type="text"
                  autocomplete="off"
                  v-if="currentStepKey === 'mother_name'"
                />

                <div v-if="currentStepKey === 'primary_email'">
                  <p class="text-subdued mb-3">
                    {{
                      $t("user.confirmAccountData.email_secure", {
                        email: maskedEmail,
                      })
                    }}
                  </p>
                  <form-field-input
                    ref="currentField"
                    :key="'recover-step-primary_email'"
                    name="passwords.primary_email"
                    v-model="passwords.primary_email"
                    rules="required|email"
                    inputmode="email"
                    type="email"
                    autocomplete="off"
                  />
                </div>

                <b-button
                  variant="primary"
                  class="btn w-full"
                  @click="handlePrimaryAction()"
                  pill
                >
                  {{
                    showValidateAction
                      ? $t("user.forgotAccount.validate_data")
                      : $t("user.forgotAccount.next")
                  }}
                </b-button>
              </div>
            </validation-observer>
          </div>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import * as _ from "lodash-es";
import companyMixin from "@state/company";
import { VueRecaptcha } from "vue-recaptcha";
import { notify } from "@/plugins/notification";
import * as FormComponents from "@components/forms";
import { usePasswords } from "@state/user/passwords";
import { useCurriculum } from "@state/user/curriculum";
import { ValidationObserver, ValidationProvider } from "vee-validate";

export default {
  mixins: [companyMixin],
  components: {
    VueRecaptcha,
    ValidationObserver,
    ValidationProvider,
    ...FormComponents,
  },
  setup() {
    const passwords = usePasswords();
    const curriculum = useCurriculum();

    return {
      passwords,
      curriculum,
    };
  },
  created() {
    this.company_name = this.company.config.company_name;

    if (this.$auth.check()) {
      this.$router.push({ name: "profile" });
    }

    // Reinicia o fluxo a cada entrada/refresh na página
    this.resetRecoveryFlow();
  },
  data() {
    return {
      stepIndex: 0,
      steps: ["cpf"],
      captchaToken: "",
      showCaptchaAlert: false,
      robot: true,
      invalidationMessage: false,
      company_name: "",
      maskedEmail: "",
    };
  },
  computed: {
    currentStepKey() {
      return this.steps[this.stepIndex] || "cpf";
    },
    isLastStep() {
      return this.stepIndex >= this.steps.length - 1;
    },
    // No passo CPF ainda precisamos carregar os campos de verificação
    showValidateAction() {
      return this.currentStepKey !== "cpf" && this.isLastStep;
    },
  },
  methods: {
    handlePrimaryAction() {
      if (this.currentStepKey === "cpf" || !this.isLastStep) {
        this.nextStep();
        return;
      }

      this.submitForm();
    },
    resetRecoveryFlow() {
      this.passwords.cpf = null;
      this.passwords.cellphone = null;
      this.passwords.birth_date = null;
      this.passwords.mother_name = null;
      this.passwords.primary_email = null;
      this.passwords.sensitive.new_email = null;
      this.passwords.sensitive.new_email_confirmation = null;
      this.maskedEmail = "";
      this.stepIndex = 0;
      this.steps = ["cpf"];
      this.invalidationMessage = false;
      this.captchaToken = "";
      this.showCaptchaAlert = false;
      this.robot = true;
    },
    captchaVerified(response) {
      if (response) {
        this.captchaToken = response;
        this.robot = false;
        this.showCaptchaAlert = false;
      }
    },
    captchaExpired() {
      this.showCaptchaAlert = true;
      this.recaptchaToken = "";
      this.$refs.recaptcha.reset();
    },
    captchaError() {
      this.showCaptchaAlert = true;
      this.captchaToken = false;
      this.robot = true;
      this.$refs.recaptcha.reset();
    },
    validateCurrentStep() {
      const field = this.$refs.currentField;

      if (!field || !field.$refs.validator) {
        return Promise.resolve(false);
      }

      return field.$refs.validator.validate().then(({ valid }) => valid);
    },
    resetVerificationFields() {
      this.passwords.cellphone = null;
      this.passwords.birth_date = null;
      this.passwords.mother_name = null;
      this.passwords.primary_email = null;
      this.maskedEmail = "";
    },
    loadVerificationSteps() {
      this.$root.loading = true;
      this.resetVerificationFields();
      this.invalidationMessage = false;

      return this.curriculum
        .getRegistrationData({
          cpf: this.passwords.cpf,
          recaptchaToken: this.captchaToken || undefined,
        })
        .then((response) => {
          if (!response.data.success || !response.data.has_verification_fields) {
            this.invalidationMessage = true;
            return;
          }

          const steps = ["cpf"];
          const data = response.data;

          if (data.cellphone) {
            steps.push("cellphone");
          }

          if (data.birth_date) {
            steps.push("birth_date");
          }

          if (data.has_mother_name) {
            steps.push("mother_name");
          }

          if (data.email) {
            steps.push("primary_email");
            this.maskedEmail = data.email;
          }

          this.steps = steps;
          this.stepIndex = 1;
        })
        .catch(() => {
          this.invalidationMessage = true;
        })
        .finally(() => {
          this.$root.loading = false;
        });
    },
    nextStep() {
      this.validateCurrentStep().then((success) => {
        if (!success) {
          return;
        }

        if (this.currentStepKey === "cpf") {
          this.loadVerificationSteps();
          return;
        }

        if (!this.isLastStep) {
          this.stepIndex++;
        }
      });
    },
    submitForm() {
      this.validateCurrentStep().then((success) => {
        if (success) {
          this.$root.loading = true;

          this.passwords
            .validateInfo()
            .then((response) => {
              if (response.data.success) {
                this.$router.push({
                  name: "reset-email",
                  params: { cpf: this.passwords.cpf },
                });

                return;
              }

              notify({
                title: "$t:errors.title.danger",
                message: "$t:errors.account_recover.not-found",
                variant: "danger",
              });

              this.invalidationMessage = true;
            })
            .catch((error) => {
              notify({
                title: "$t:errors.title.danger",
                message: "$t:errors.account_recover.not-found",
                variant: "danger",
              });

              this.invalidationMessage = true;
              this.$root.loading = false;
            })
            .finally(() => {
              this.$root.loading = false;
            });
        }
      });
    },
  },
};
</script>
